<template>
  <div
    class="bitts-table__column-header"
    :class="{
      'cursor-pointer': params.enableSorting,
      'cursor-default': !params.enableSorting,
      'manual-sort-ascending': showManualDescSort,
    }"
    role="presentation"
    @click="onSortRequested"
  >
    <div v-if="!!params.showCheckbox">
      <BittsCheckbox
        class="mr-12"
        :checked="headerChecked"
        :indeterminate="headerIndeterminate"
        :aria-label="params.checkboxAriaLabel"
        @click.stop
        @input="(bool: boolean) => params.onChecked?.(bool)"
      />
    </div>
    <div
      :class="
        sortEnabled || manualSortEnabled
          ? 'text-secondary-text'
          : 'text-neutral-text-strong'
      "
      class="mr-4 truncate"
    >
      <div class="flex items-center justify-between">
        <BittsSvg
          v-if="params.icon && typeof params.icon === 'string'"
          :svg="params.icon"
          class="h-16 w-16 mr-2"
        />
        <FontAwesomeIcon
          v-else-if="params.icon"
          :icon="params.icon"
          class="h-16 w-16 mr-2"
        />
        <BittsTooltip
          v-if="params.showTooltip"
          :mount-to-body="true"
          placement="bottom"
          class="max-w-max truncate"
        >
          <p class="truncate">
            {{ params.displayName }}
          </p>
          <template #title>
            <span>{{ params.displayName }}</span>
          </template>
        </BittsTooltip>
        <span v-else>{{ params.displayName }}</span>
      </div>
    </div>
    <div
      v-if="
        (params.showArrow && !sortEnabled) ||
        (params.onManualSort && !manualSortEnabled)
      "
    >
      <FontAwesomeIcon
        class="c-bitts-table__sort-toggle text-neutral-accent"
        :icon="['fak', 'arrow-down']"
        :style="{ height: '12px', width: '12px' }"
      />
    </div>
    <div
      v-if="(params.enableSorting && sortEnabled) || manualSortEnabled"
      class="text-secondary-accent"
    >
      <FontAwesomeIcon
        class="c-bitts-table__sort-toggle"
        :icon="['fak', 'arrow-down']"
        :style="{ height: '12px', width: '12px', color: 'currentColor' }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IHeaderParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import BittsCheckbox from '../BittsCheckbox/BittsCheckbox.vue';
import BittsSvg from '../BittsSvg/BittsSvg.vue';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';
import type { BittsIcon } from '../types';

type ManualSortOrder = 'asc' | 'desc';
type ManualSortPayload = { sort: string; order: ManualSortOrder };

export type CustomColumnHeaderParams = IHeaderParams & {
  showCheckbox?: boolean;
  checkboxAriaLabel?: string;
  checked?: boolean;
  indeterminate?: boolean;
  getChecked?: () => boolean;
  getIndeterminate?: () => boolean;
  onChecked?: (checked: boolean) => void;
  icon?: BittsIcon | string;
  showTooltip?: boolean;
  showArrow?: boolean;
  onManualSort?: (payload: ManualSortPayload) => void;
};

const { params } = defineProps<{
  params: CustomColumnHeaderParams;
}>();

const ascSort = ref<boolean | null>(null);
const descSort = ref<boolean | null>(null);
const noSort = ref<boolean | null>(null);

const headerChecked = computed(() =>
  typeof params.getChecked === 'function'
    ? params.getChecked()
    : !!params.checked,
);
const headerIndeterminate = computed(() =>
  typeof params.getIndeterminate === 'function'
    ? params.getIndeterminate()
    : !!params.indeterminate,
);

const route = useRoute();

const ASC: ManualSortOrder = 'asc';
const DESC: ManualSortOrder = 'desc';

const sortEnabled = computed(() => ascSort.value || descSort.value);
const manualSortSameCol = computed(
  () => route?.query?.sort === params.column.getColId(),
);

const showManualDescSort = computed(
  () => manualSortSameCol.value && route?.query?.order === ASC,
);

const showManualAscSort = computed(
  () => manualSortSameCol.value && route?.query?.order === DESC,
);

const manualSortEnabled = computed(
  () => showManualDescSort.value || showManualAscSort.value,
);

function onManualSortChanged(init: unknown = null) {
  /* This event listener callback is triggered on first page load.
      We only want to trigger the onManualSort when a user clicks a sortable header cell.
      The results of an existing query or no query at all on page load will already be present from response */
  if (init) return;

  const payload: ManualSortPayload = {
    sort: params.column.getColId(),
    order: showManualAscSort.value ? ASC : DESC,
  };

  params.onManualSort?.(payload);
}

function onSortRequested() {
  if (!params.enableSorting) return;
  if (params.onManualSort) onManualSortChanged();
  else params.progressSort();
}

function onSortChanged() {
  ascSort.value = descSort.value = noSort.value = false;
  const sort = params.column.getSort();
  if (sort === ASC) {
    ascSort.value = true;
  } else if (sort === DESC) {
    descSort.value = true;
  } else {
    noSort.value = true;
  }
}

onMounted(() => {
  const { enableSorting, onManualSort, column } = params;

  if (enableSorting) {
    /* onManualSort is a function that is passed in from the parent via column definitions
  (columns prop passed to BittsTable). It is used to handle manual sorting via router
  If onManualSort is passed in, we need to add a listener for manual sort. Otherwise, we add a listener for default sort.
  The onManualSort passes the { sort, order } payload to the parent. */
    const eventListener = onManualSort ? onManualSortChanged : onSortChanged;
    column.addEventListener('sortChanged', eventListener);
    eventListener(onManualSort);
  }
});

onBeforeUnmount(() => {
  params.column.removeEventListener('sortChanged', onSortChanged);
  if (params.onManualSort)
    params.column.removeEventListener('sortChanged', onManualSortChanged);
});
</script>
